/**
 * PATH: packages/email-hub/src/feature/hooks/useLiveSearch.ts
 * FILE_ID: EMAILHUB-FEATURE-USE-LIVE-SEARCH-001
 * PURPOSE: G2 live Outlook fanout ? explicit, user-triggered Graph
 *          $search via GET /api/emails/live-search/ when local search
 *          recall is sparse.
 * WHY: The local index only covers synced history. Rather than
 *      auto-fanning to Graph on every keystroke (latency + quota),
 *      the hook gates on sparse local results AND an explicit click
 *      of the "Search Outlook for older mail" affordance.
 * FLOW:
 *   searchActive + localCount < threshold ? offer CTA
 *      ? user clicks trigger() ? GET live-search ? results/error
 *      ? query change resets to idle (stale fanout never shown).
 * DEPENDENCIES:
 *   - ../lib/api emailApi: tenant-wired axios seam (same as useEmails).
 * RELATED:
 *   - components/LiveSearchResults.tsx ? renders the states.
 *   - lib/syncSearchBanner.ts ? banner-side CTA gate.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { emailApi } from '../lib/api'

/** Local first-page result count below which we offer the Outlook CTA. */
export const SPARSE_LOCAL_RESULT_THRESHOLD = 5

export interface LiveSearchResultRow {
  graph_id: string
  subject: string
  from_name: string
  from_address: string
  received_at: string | null
  body_preview: string
  has_attachments: boolean
  account_id: number
  account_email: string
  synced: boolean
  email_id: string | null
}

export interface LiveSearchResponse {
  results: LiveSearchResultRow[]
  accounts_searched: { account_id: number; account_email: string }[]
  accounts_skipped: {
    account_id: number
    account_email: string
    reason: string
  }[]
}

export type LiveSearchStatus = 'idle' | 'loading' | 'success' | 'error'

/**
 * f(search state) ? should the "Search Outlook for older mail" CTA be
 * offered? Pure so it is unit-testable: only when a search is active,
 * the local page has settled (not loading), and recall is sparse.
 * NEVER auto-fires the fanout ? offering ? searching.
 */
export function shouldOfferLiveSearch(args: {
  searchActive: boolean
  localResultCount: number
  localLoading: boolean
}): boolean {
  const { searchActive, localResultCount, localLoading } = args
  if (!searchActive) return false
  if (localLoading) return false
  return localResultCount < SPARSE_LOCAL_RESULT_THRESHOLD
}

export function useLiveSearch(args: {
  /** Debounced (250ms) search text ? the same value the inbox queries with. */
  query: string
  searchActive: boolean
  localResultCount: number
  localLoading: boolean
}) {
  const { query, searchActive, localResultCount, localLoading } = args
  const [status, setStatus] = useState<LiveSearchStatus>('idle')
  const [data, setData] = useState<LiveSearchResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Monotonic request id ? a stale fanout resolving after the query
  // changed must never overwrite the reset-to-idle state.
  const requestSeq = useRef(0)

  const offered = shouldOfferLiveSearch({
    searchActive,
    localResultCount,
    localLoading,
  })

  // Query changed (or search cleared) ? any previous fanout is stale.
  useEffect(() => {
    requestSeq.current += 1
    setStatus('idle')
    setData(null)
    setError(null)
  }, [query])

  const trigger = useCallback(async () => {
    const q = query.trim()
    if (q.length < 2) return
    const seq = ++requestSeq.current
    setStatus('loading')
    setError(null)
    try {
      const sp = new URLSearchParams({ q })
      const resp = await emailApi<LiveSearchResponse>(
        `/api/emails/live-search/?${sp.toString()}`,
      )
      if (requestSeq.current !== seq) return
      setData(resp)
      setStatus('success')
    } catch (err) {
      if (requestSeq.current !== seq) return
      const message =
        err instanceof Error ? err.message : 'Outlook search failed'
      setError(message)
      setStatus('error')
    }
  }, [query])

  return { offered, status, data, error, trigger }
}
